import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import {
  loyaltyProgramResponseSchema,
  loyaltyProgramShowResponseSchema,
} from '@rd/schemas/loyalty-program.schema';
import { loyaltyCurrencySerializer } from '@rd/serializers/loyalty-currency.serializer';
import { loyaltyProgramCapabilitySerializer } from '@rd/serializers/loyalty-program-capability.serializer';
import { transferRateSerializer } from '@rd/serializers/transfer-rate.serializer';
import {
  getCapability,
  LoyaltyProgram,
  LoyaltyProgramCapabilities,
} from '@rd/types';
import { LoyaltyCurrencyModel } from '@rd/types/loyalty-currency.model';
import { LoyaltyProgramCapabilityModel } from '@rd/types/loyalty-program-capability.model';
import { LoyaltyProgramMembershipValidationResponse } from '@rd/types/loyalty-program-membership-validation.response';
import { LoyaltyProgramModel } from '@rd/types/loyalty-program.model';
import {
  LoyaltyProgramResponse,
  LoyaltyProgramShowResponse,
} from '@rd/types/loyalty-program.response';
import { RewardsProgramModel } from '@rd/types/rewards-program.model';
import { TransferRateModel } from '@rd/types/transfer-rate.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramApiService {
  private apiValidationService = inject(ApiValidationService);
  private nydusNetworkDataService = inject(NydusNetworkApiDataService);
  private sentryService = inject(SentryService);

  private isModelCapability(
    model:
      | LoyaltyProgramCapabilityModel
      | LoyaltyCurrencyModel
      | TransferRateModel
      | RewardsProgramModel,
  ): model is LoyaltyProgramCapabilityModel {
    return model.type === 'capability';
  }

  private isModelLoyaltyCurrency(
    model:
      | LoyaltyProgramCapabilityModel
      | LoyaltyCurrencyModel
      | TransferRateModel
      | RewardsProgramModel,
  ): model is LoyaltyCurrencyModel {
    return model.type === 'loyalty_currency';
  }

  private isModelTransferRate(
    model:
      | RewardsProgramModel
      | LoyaltyProgramCapabilityModel
      | LoyaltyCurrencyModel
      | TransferRateModel,
  ): model is TransferRateModel {
    return (
      model.type === 'points_transfer_rate' ||
      model.type === 'cash_transfer_rate'
    );
  }

  private isModelRewardsProgram(
    model:
      | LoyaltyProgramCapabilityModel
      | LoyaltyCurrencyModel
      | RewardsProgramModel
      | TransferRateModel,
  ): model is RewardsProgramModel {
    return model.type === 'rewards_program';
  }

  getAllLoyaltyPrograms(): Observable<LoyaltyProgram[]> {
    const params = new HttpParams().set('sort', 'transfer_connect_identifier');
    return this.nydusNetworkDataService
      .get<LoyaltyProgramResponse>('loyalty_programs', params)
      .pipe(
        map((loyaltyProgramsResponse) => {
          this.apiValidationService.validate<LoyaltyProgramResponse>({
            apiName: `loyalty_programs?${decodeURI(params.toString())}`,
            schema: loyaltyProgramResponseSchema,
            response: loyaltyProgramsResponse,
          });
          return this.transformLoyaltyProgramsResponse(loyaltyProgramsResponse);
        }),
      );
  }

  getLoyaltyPrograms(
    params: HttpParams = this.buildLoyaltyProgramParams(),
  ): Observable<LoyaltyProgram[]> {
    return this.nydusNetworkDataService
      .get<LoyaltyProgramResponse>('loyalty_programs', params)
      .pipe(
        map((loyaltyProgramsResponse) => {
          this.apiValidationService.validate<LoyaltyProgramResponse>({
            apiName: `loyalty_programs?${decodeURI(params.toString())}`,
            schema: loyaltyProgramResponseSchema,
            response: loyaltyProgramsResponse,
          });
          return this.transformLoyaltyProgramsResponse(loyaltyProgramsResponse);
        }),
      );
  }

  getLoyaltyProgram(loyaltyProgramId: string): Observable<LoyaltyProgram> {
    return this.nydusNetworkDataService
      .get<LoyaltyProgramShowResponse>(`loyalty_programs/${loyaltyProgramId}`)
      .pipe(
        map((loyaltyProgramShowResponse) => {
          this.apiValidationService.validate<LoyaltyProgramShowResponse>({
            apiName: `loyalty_programs/${loyaltyProgramId}`,
            schema: loyaltyProgramShowResponseSchema,
            response: loyaltyProgramShowResponse,
          });
          return this.transformLoyaltyProgramShowResponse(
            loyaltyProgramShowResponse,
          );
        }),
      );
  }

  getRedeemCashLoyaltyPrograms(): Observable<LoyaltyProgram[]> {
    return this.nydusNetworkDataService
      .get<LoyaltyProgramResponse>(
        'loyalty_programs',
        this.buildRedeemCashLoyaltyProgramParams(),
      )
      .pipe(
        map((loyaltyProgramsResponse) => {
          this.apiValidationService.validate<LoyaltyProgramResponse>({
            apiName: `loyalty_programs?filter[category][$eq]=cash,cashback_to_card,cashback_to_account,cashback_to_platform`,
            schema: loyaltyProgramResponseSchema,
            response: loyaltyProgramsResponse,
          });
          return this.transformLoyaltyProgramsResponse(loyaltyProgramsResponse);
        }),
      );
  }

  private buildLoyaltyProgramParams(): HttpParams {
    // sort_index is a numeric column in NN used to custom sort the programs
    const params = new HttpParams()
      .set('sort', 'sort_index')
      .set('filter[category][$eq]', 'hotel,airline');

    return params;
  }

  private buildRedeemCashLoyaltyProgramParams(): HttpParams {
    const params = new HttpParams()
      .set('sort', 'transfer_connect_identifier')
      .set(
        'filter[category][$eq]',
        'cash,cashback_to_card,cashback_to_account,cashback_to_platform',
      );

    return params;
  }

  private transformLoyaltyProgramsResponse(
    loyaltyProgramResponse: LoyaltyProgramResponse,
  ): LoyaltyProgram[] {
    return loyaltyProgramResponse.data.map((loyaltyProgramModel) =>
      this.transformLoyaltyProgramModel(
        loyaltyProgramModel,
        loyaltyProgramResponse.included,
      ),
    );
  }

  private transformLoyaltyProgramShowResponse(
    loyaltyProgramShowResponse: LoyaltyProgramShowResponse,
  ): LoyaltyProgram {
    return this.transformLoyaltyProgramModel(
      loyaltyProgramShowResponse.data,
      loyaltyProgramShowResponse.included,
    );
  }

  private transformLoyaltyProgramModel(
    loyaltyProgramModel: LoyaltyProgramModel,
    models: (
      | LoyaltyCurrencyModel
      | LoyaltyProgramCapabilityModel
      | TransferRateModel
      | RewardsProgramModel
    )[],
  ): LoyaltyProgram {
    const loyaltyCurrencyModels = models.filter(this.isModelLoyaltyCurrency);
    const associatedLoyaltyCurrency = loyaltyCurrencyModels.find(
      (lc) =>
        lc.id === loyaltyProgramModel.relationships.loyaltyCurrency?.data?.id,
    );
    const { id, attributes } = loyaltyProgramModel;
    /* eslint-disable-next-line max-len */
    const {
      category,
      name,
      shortName,
      description,
      transferConnectIdentifier,
      manualVerificationRequired,
      sortIndex,
    } = attributes;

    const capabilityIds: Set<string> = new Set(
      loyaltyProgramModel.relationships.capabilities?.data.map(
        (val) => val.id,
      ) ?? [],
    );

    const transferRateModelsMap = new Map(); // Map<LoyaltyCurrencyId, TransferRate>

    const transferRateModels: TransferRateModel[] = models.filter(
      this.isModelTransferRate,
    );
    for (const transferRateModel of transferRateModels) {
      transferRateModelsMap.set(
        transferRateModel.attributes.loyaltyCurrencyId,
        transferRateSerializer(transferRateModel),
      );
    }

    let capabilities = models
      .filter(this.isModelCapability)
      .filter((capability) => capabilityIds.has(capability.id))
      .map(loyaltyProgramCapabilitySerializer);

    let transformedLoyaltyProgram: LoyaltyProgram = {
      category,
      description,
      id,
      name,
      shortName,
      transferConnectIdentifier,
      manualVerificationRequired,
      sortIndex,
      capabilities,
      // @ts-ignore
      transferRate: null, // Make it null first, inject later
      pointsTransferCapability: getCapability(
        capabilities,
        LoyaltyProgramCapabilities.PointsTransfer,
        this.sentryService,
      ),
      benefitCapability: getCapability(
        capabilities,
        LoyaltyProgramCapabilities.Benefit,
        this.sentryService,
      ),
      cashBackToCardCapability: getCapability(
        capabilities,
        LoyaltyProgramCapabilities.CashBackToCard,
        this.sentryService,
      ),
      cashBackToAccountCapability: getCapability(
        capabilities,
        LoyaltyProgramCapabilities.CashBackToAccount,
        this.sentryService,
      ),
      cashBackToPlatformCapability: getCapability(
        capabilities,
        LoyaltyProgramCapabilities.CashBackToPlatform,
        this.sentryService,
      ),
    };

    if (associatedLoyaltyCurrency) {
      transformedLoyaltyProgram.loyaltyCurrency = loyaltyCurrencySerializer(
        associatedLoyaltyCurrency,
      );
    }

    transformedLoyaltyProgram = this.injectRewardsProgram(
      transformedLoyaltyProgram,
      loyaltyProgramModel.relationships,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      models.filter(this.isModelRewardsProgram)![0],
    );

    // We inject transfer rate after loyalty currency because we need to know the currency id
    transformedLoyaltyProgram.transferRate = transferRateModelsMap.get(
      transformedLoyaltyProgram.loyaltyCurrency?.id,
    );

    return transformedLoyaltyProgram;
  }

  private injectRewardsProgram(
    transformedLoyaltyProgram: LoyaltyProgram,
    relationships: LoyaltyProgramModel['relationships'],
    rewardProgramModel: RewardsProgramModel,
  ): LoyaltyProgram {
    if (relationships?.rewardsProgram?.data) {
      const rewardsProgram = {
        id: rewardProgramModel.id,
        description: rewardProgramModel.attributes.description,
        data: rewardProgramModel.attributes.data,
        externalLink: rewardProgramModel.attributes.externalLink,
      };
      return {
        ...transformedLoyaltyProgram,
        rewardsProgram,
      };
    }
    return transformedLoyaltyProgram;
  }

  postMembershipValidation(
    loyaltyProgramId: string,
    membershipNumber: string,
  ): Observable<boolean> {
    return this.nydusNetworkDataService
      .post<LoyaltyProgramMembershipValidationResponse>(
        `loyalty_programs/${loyaltyProgramId}/membership_validation`,
        {
          membershipValidation: {
            number: membershipNumber,
          },
        },
      )
      .pipe(
        map((response) => {
          return this.transformMembershipValidationResponse(response);
        }),
      );
  }

  private transformMembershipValidationResponse(
    response: LoyaltyProgramMembershipValidationResponse,
  ): boolean {
    return response.data.attributes.valid;
  }
}
